interface AdlWithCarePlan {
  carePlan?: { id?: string }
}

/**
 * Defence-in-depth guard against linking one resident's care plans to another
 * resident's record (e.g. when an in-flight selection survives a quick-switch).
 *
 * Returns the ids unchanged unless the ADL slot is authoritative for
 * `residentId`. Authoritative means: it was loaded for this resident
 * (`adlsResidentId === residentId`) AND the data has actually arrived
 * (`adls` is an array, not null/undefined — `fetchResidentAdls` sets the
 * resident id before the data lands, so a null slot still means "loading").
 *
 * Once authoritative we filter to the resident's care plans, INCLUDING the
 * empty case: a resident with no care plans means every selected id is foreign
 * and must be stripped.
 */
export const rejectForeignCarePlanIds = (
  selectedIds: string[],
  adls: AdlWithCarePlan[] | null | undefined,
  adlsResidentId: string | undefined,
  residentId: string | undefined
): string[] => {
  if (!residentId || adlsResidentId !== residentId) {
    return selectedIds
  }

  // Not loaded yet — can't validate, so don't drop anything.
  if (adls == null) {
    return selectedIds
  }

  const validIds = new Set(
    adls
      .map((adl) => adl.carePlan?.id)
      .filter((id): id is string => Boolean(id))
  )

  return selectedIds.filter((id) => validIds.has(id))
}
